/**
 * HTTP layer for the `nodesMetadata.json` sidecar. Mirrors the routing
 * pattern in {@link draftWorkflowApi}:
 *   - Local mode: Vite plugin endpoint `/local/workflows/:name/nodesMetadata`
 *   - Cloud mode: VFS PUT/GET next to the workflow at
 *     `${vfsBase}/${workflowName}/nodesMetadata.json`
 *
 * The sidecar is a single file containing both `draft` and `published`
 * branches — see `services/nodesMetadata.ts` for the shape. Callers
 * should fetch on workflow load and PUT alongside workflow saves.
 */

import {
  isLocalMode,
  getLocalPluginUrl,
  localFetch,
} from './localRuntimeClient';
import { AppContext, appFetch, getNewVfsUrl } from './appClient';
import { apiVersions } from './endpoints';
import { telemetryService } from '@/services/telemetryService';
import {
  type NodesMetadataFile,
  parseNodesMetadata,
} from './nodesMetadata';

export type NodesMetadataOptions = AppContext & {
  workflowName: string;
};

const SIDECAR_FILE = 'nodesMetadata.json';

function cloudUrl(opts: NodesMetadataOptions): string {
  const vfsBase = getNewVfsUrl(opts);
  return `${vfsBase}/${opts.workflowName}/${SIDECAR_FILE}?api-version=${apiVersions.otto}`;
}

function localUrl(opts: NodesMetadataOptions): string {
  return `${getLocalPluginUrl()}/local/workflows/${opts.workflowName}/nodesMetadata`;
}

/**
 * Fetch the sidecar. Returns `null` when the file does not exist (first save
 * for this workflow) or when the response cannot be parsed.
 */
export async function getNodesMetadata(
  opts: NodesMetadataOptions,
): Promise<NodesMetadataFile | null> {
  if (isLocalMode()) {
    try {
      // Plugin returns 404 (with a JSON error body) when the sidecar file is
      // absent or unreadable; localFetch throws on non-2xx, so the common
      // "no sidecar yet" case falls into the catch below and yields null.
      const raw = await localFetch<unknown>(localUrl(opts));
      if (raw == null) return null;
      const parsed = parseNodesMetadata(raw);
      // Treat a sidecar that was written but never populated
      // (`emptyNodesMetadata()` shape: updatedAt === 0, no node entries on
      // either branch) as if it didn't exist, so callers fall back to
      // auto-layout instead of overlaying an empty layout.
      return parsed.updatedAt === 0 && !parsed.draft.nodes && !parsed.published.nodes
        ? null
        : parsed;
    } catch {
      return null;
    }
  }

  const accessToken = await opts.getToken();
  if (!accessToken) return null;

  try {
    const res = await appFetch<unknown>(cloudUrl(opts), accessToken, { method: 'GET' });
    if (res == null) return null;
    return parseNodesMetadata(res);
  } catch {
    // 404 (no sidecar yet) is the common case — silently fall through.
    return null;
  }
}

/**
 * Write the sidecar. Returns `true` on success. Failures are logged via
 * telemetry but don't throw — the workflow save shouldn't fail because the
 * sidecar PUT couldn't complete (the workflow itself is the contract).
 */
export async function saveNodesMetadata(
  opts: NodesMetadataOptions,
  file: NodesMetadataFile,
): Promise<boolean> {
  const start = performance.now();

  if (isLocalMode()) {
    try {
      await localFetch(localUrl(opts), { method: 'POST', body: file });
      telemetryService.trackMetric('api.response_time', performance.now() - start, {
        endpoint: 'saveNodesMetadata',
        method: 'POST',
        statusCode: '200',
      });
      return true;
    } catch (err) {
      telemetryService.trackEvent('api.error', {
        endpoint: 'saveNodesMetadata',
        method: 'POST',
        statusCode: 'unknown',
        error: (err instanceof Error ? err.message : String(err)).slice(0, 200),
      });
      return false;
    }
  }

  const accessToken = await opts.getToken();
  if (!accessToken) return false;

  try {
    await appFetch(cloudUrl(opts), accessToken, {
      method: 'PUT',
      headers: { 'If-Match': '*' },
      body: JSON.stringify(file),
    });
    telemetryService.trackMetric('api.response_time', performance.now() - start, {
      endpoint: 'saveNodesMetadata',
      method: 'PUT',
      statusCode: '200',
    });
    return true;
  } catch (err) {
    telemetryService.trackEvent('api.error', {
      endpoint: 'saveNodesMetadata',
      method: 'PUT',
      statusCode: 'unknown',
      error: (err instanceof Error ? err.message : String(err)).slice(0, 200),
    });
    return false;
  }
}
